import { supabase } from '../supabase';
import { Property } from '../types/property';

/**
 * Helper function to safely truncate strings to fit database column limits
 */
function safeTruncate(value: string | null | undefined, maxLength: number): string | null {
  if (!value) return null;
  const stringValue = String(value);
  return stringValue.length > maxLength ? stringValue.substring(0, maxLength) : stringValue;
}

/**
 * Helper function to safely convert to decimal with validation
 */
function safeDecimal(value: any, precision: number = 10, scale: number = 2): number | null {
  if (value === null || value === undefined) return null;
  const num = parseFloat(value);
  return isNaN(num) ? null : Number(num.toFixed(scale));
}

/**
 * Update existing property using REST API
 */
async function updateExistingProperty(rentalPropertyData: any, supabaseUrl: string, supabaseAnonKey: string): Promise<number | null> {
  try {
    const updateUrl = `${supabaseUrl}/rest/v1/rental_properties?zpid=eq.${rentalPropertyData.zpid}`;
    

    
    const updateResp = await fetch(updateUrl, {
      method: 'PATCH',
      headers: {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(rentalPropertyData)
    });
    
    if (!updateResp.ok) {
      const errorText = await updateResp.text();

      throw new Error(`REST update failed with status ${updateResp.status}: ${errorText}`);
    }
    

    
    // Try to get the ID from response
    try {
      const responseData = await updateResp.json();
      if (responseData && Array.isArray(responseData) && responseData[0] && responseData[0].id) {

        return responseData[0].id;
      }
    } catch (parseError) {

    }
    
    return null;
  } catch (error) {

    throw error;
  }
}

/**
 * Upsert rental property to rental_properties table
 */
export async function upsertRentalProperty(property: Property): Promise<number | null> {
  // Check if we've recently processed this property to avoid duplicate 409 errors
  const now = Date.now();
  const sourceId = property.source_id;
  if (sourceId && recentlyProcessed.has(sourceId)) {
    const lastProcessed = recentlyProcessed.get(sourceId)!;
    if (now - lastProcessed < PROCESSING_COOLDOWN) {
      return null; // Skip processing if recently processed
    }
  }

  try {
    // Validate that we have a source_id before attempting upsert
    const sourceIdString = String(property.source_id || '');
    if (!property.source_id || sourceIdString.trim() === '') {
      // Generate a fallback ID for properties without source_id instead of skipping
      const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      property.source_id = fallbackId;
    }

    // Extract rental metadata if available - be more flexible with missing data
    const rentalMetadata = (property as any).rentalMetadata || {};
    
    // Price range data prepared for database storage
    
    // Ensure we have basic property data even if some fields are missing
    if (!property.title || property.title.trim() === '') {
      property.title = `Rental Property at ${property.address || 'Unknown Location'}`;
    }
    
    if (!property.address || property.address.trim() === '') {
      property.address = 'Address not available';
    }
    
    // Prepare rental property data with proper length validation
    const rentalPropertyData = {
      zpid: safeTruncate(sourceIdString, 50),
      provider_listing_id: safeTruncate(rentalMetadata.providerListingID, 100),
      title: safeTruncate(property.title, 255),
      description: property.description || '', // TEXT field, no length limit
      address_street: safeTruncate(property.address, 255),
      city: safeTruncate(property.city, 100),
      state: safeTruncate(property.state, 10), // This was the issue - VARCHAR(10)
      zipcode: safeTruncate(property.zipCode, 20), // FIXED: Map from property.zipCode to database zipcode column
      latitude: property.location?.lat ? Number(property.location.lat.toFixed(8)) : null,
      longitude: property.location?.lon ? Number(property.location.lon.toFixed(8)) : null,
      building_id: safeTruncate(rentalMetadata.buildingId, 100),
      home_type: safeTruncate(property.type?.toUpperCase(), 100),
      group_type: safeTruncate(rentalMetadata.groupType, 100),
      listing_status: 'FOR_RENT', // Fixed value
      listing_source: 'Zillow',
      listing_datetime_on_zillow: rentalMetadata.listingDateTimeOnZillow || null,
      days_on_zillow: (() => {
        const daysOnZillow = (property as any).daysOnZillow || (property as any).days_on_zillow;
        // Ensure we only pass integers to the INTEGER column
        if (typeof daysOnZillow === 'number') return daysOnZillow;
        if (typeof daysOnZillow === 'string') {
          // Handle strings like "13 days", "13", etc.
          const match = daysOnZillow.match(/\d+/);
          const result = match ? parseInt(match[0], 10) : null;
          return result;
        }
        return null;
      })(),
      min_price: (() => {
        // Check for price range data in rentalMetadata or priceRange
        const rentalMetadata = (property as any).rentalMetadata;
        const priceRange = (property as any).priceRange;
        
        if (rentalMetadata?.hasPriceRange && rentalMetadata?.minPrice) {
          return safeDecimal(rentalMetadata.minPrice);
        } else if (priceRange?.min) {
          return safeDecimal(priceRange.min);
        } else {
          return safeDecimal(property.price);
        }
      })(),
      max_price: (() => {
        // Check for price range data in rentalMetadata or priceRange
        const rentalMetadata = (property as any).rentalMetadata;
        const priceRange = (property as any).priceRange;
        
        if (rentalMetadata?.hasPriceRange && rentalMetadata?.maxPrice) {
          return safeDecimal(rentalMetadata.maxPrice);
        } else if (priceRange?.max) {
          return safeDecimal(priceRange.max);
        } else {
          return safeDecimal(property.price);
        }
      })(),
      price_per_square_foot: null, // Calculate if needed
      year_built: property.details?.yearBuilt ? Number(property.details.yearBuilt) : null,
      lot_size: safeDecimal(property.details?.lotSize?.amount),
      lot_size_units: null, // Not available in Property type
      square_feet: property.details?.squareFeet ? Number(property.details.squareFeet) : null,
      bedrooms: property.details?.bedrooms || 0,
      bathrooms: property.details?.bathrooms || 1,
      rental_date_available: null, // Not available in search results
      move_in_ready: true, // Assume available
      is_income_restricted: false, // Default value
      is_housing_connector: false, // Default value
      is_zillow_owned: false, // Default value
      is_featured: rentalMetadata.isFeatured || false,
      is_showcase_listing: rentalMetadata.isShowcaseListing || false,
      is_room_for_rent: rentalMetadata.isRoomForRent || false,
      is_build_to_rent: rentalMetadata.isBuildToRent || false,
      matching_home_count: rentalMetadata.matchingHomeCount || 0,
      best_guess_timezone: rentalMetadata.bestGuessTimeZone || null,
      currency: safeTruncate(rentalMetadata.currency, 10),
      country: safeTruncate(rentalMetadata.country, 10),
      marketing_treatments: Array.isArray(rentalMetadata.marketingTreatments) ? rentalMetadata.marketingTreatments : [],
      // Store photos directly in the photos array column
      photos: property.photos && property.photos.length > 0 ? property.photos : [],
      primary_photo_url: property.photos?.[0] || property.imageUrl || null,
      street_view_image_url: null, // Not available in search results
      photo_count: property.photos?.length || 0,
      has_vr_model: property.has_virtual_tour || false,
      has_videos: property.has_virtual_tour || false,
      posting_url: property.url || null,
      schedule_tour_url: null, // Not available in search results
      contact_agent_url: null, // Not available in search results
      start_offer_url: null, // Not available in search results
      favorite_count: 0, // Default value
      page_view_count: 0, // Default value
      contact_count: 0, // Default value
      application_count: 0, // Default value
      last_sync: new Date().toISOString()
    };

    // Validate required fields before sending - be more lenient
    if (!rentalPropertyData.zpid || rentalPropertyData.zpid.trim() === '') {
      return null;
    }
    
    // Validate address components - now that we're properly extracting from API, be more strict
    if (!rentalPropertyData.city || rentalPropertyData.city.trim() === '') {
      // Don't set fallback - let it be NULL if not available
    }
    
    if (!rentalPropertyData.state || rentalPropertyData.state.trim() === '') {
      // Don't set fallback - let it be NULL if not available
    }
    
    if (!rentalPropertyData.zipcode || rentalPropertyData.zipcode.trim() === '') {
      // Don't set fallback - let it be NULL if not available
    }
    
    // Loosen price validation - allow 0 and null prices for rental properties
    if (rentalPropertyData.min_price === null) {
      rentalPropertyData.min_price = 0; // Set to 0 instead of skipping
    }
    if (rentalPropertyData.min_price < 0) {
      rentalPropertyData.min_price = 0;
    }

    // Check if property already exists using REST API to avoid SDK hanging
    let existingProperty = null;
    try {
      const { supabaseUrl, supabaseAnonKey } = await import('../supabase');
      const checkUrl = `${supabaseUrl}/rest/v1/rental_properties?zpid=eq.${rentalPropertyData.zpid}&select=id`;
      

      
      const checkResp = await fetch(checkUrl, {
        method: 'GET',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (checkResp.ok) {
        const checkData = await checkResp.json();
        existingProperty = checkData && checkData.length > 0 ? checkData[0] : null;

      } else {

      }
    } catch (checkError) {

    }
    
    if (existingProperty) {
      // Property already exists, will update with new data

    } else {

    }

    // Upsert to rental_properties table (will update if exists, insert if new)
    try {
      // Skip SDK and use direct REST for reliable performance
      const { supabaseUrl, supabaseAnonKey } = await import('../supabase');
      const restUrl = `${supabaseUrl}/rest/v1/rental_properties`;
      

      
      const resp = await fetch(`${restUrl}?on_conflict=zpid`, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'resolution=merge-duplicates,return=representation'
        },
        body: JSON.stringify([rentalPropertyData])
      });
      
      if (!resp.ok) {
        const errorText = await resp.text();

        // Handle 409 duplicate key error by trying an update instead
        if (resp.status === 409 && errorText.includes('duplicate key')) {
          return await updateExistingProperty(rentalPropertyData, supabaseUrl, supabaseAnonKey);
        }
        
        // For other errors, don't throw to prevent breaking the search
        
        return null; // Return null instead of throwing to prevent breaking the search
      }
      

      
      // Try to parse response body for ID (with return=representation, we should get the data back)
      try {
        const responseData = await resp.json();

        
        if (responseData && Array.isArray(responseData) && responseData[0] && responseData[0].id) {
          // Track successful processing
          if (sourceId) {
            recentlyProcessed.set(sourceId, now);
          }
          return responseData[0].id;
        }
        
        // If no array, check if it's a single object
        if (responseData && responseData.id) {
          // Track successful processing
          if (sourceId) {
            recentlyProcessed.set(sourceId, now);
          }
          return responseData.id;
        }
      } catch (parseError) {

      }
      
      // Fallback: try to get ID from location header
      const locationHeader = resp.headers.get('location');
      if (locationHeader) {
        const idMatch = locationHeader.match(/\/(\d+)$/);
        if (idMatch) {
          // Track successful processing
          if (sourceId) {
            recentlyProcessed.set(sourceId, now);
          }
          return parseInt(idMatch[1]);
        }
      }
      

      return null;
      
    } catch (restError) {

      throw restError;
    }
    

    return null; // This should not be reached, but just in case
      
    } catch (error) {
      // Additional context

      throw error;
    }
}



/**
 * Photos are now stored directly in the rental_properties.photos column
 * This function is kept for backward compatibility but now reads from the main table
 */
export async function upsertRentalPhotos(
  rentalPropertyId: number, 
  property: Property
): Promise<void> {
  // Photos are now handled directly in upsertRentalProperty
  // This function is kept for backward compatibility but does nothing
}

/**
 * Get rental photos for a property
 * Photos are now stored directly in the rental_properties.photos column
 */
export async function getRentalPhotos(rentalPropertyId: number): Promise<string[]> {
  try {
    const { data: property, error } = await supabase
      .from('rental_properties')
      .select('photos')
      .eq('id', rentalPropertyId)
      .single();
    
    if (error) {
      return [];
    }
    
    // Return the photos array or empty array if no photos
    return property?.photos || [];
  } catch (error) {
    return [];
  }
}

/**
 * Get rental units for a property
 * Rental units table has been removed - units are now stored directly in rental_properties
 */
export async function getRentalUnits(rentalPropertyId: number): Promise<any[]> {
  try {
    // Since rental_units table is removed, return basic unit info from rental_properties
    const { data: property, error } = await supabase
      .from('rental_properties')
      .select('bedrooms, bathrooms, square_feet, min_price, max_price')
      .eq('id', rentalPropertyId)
      .single();
    
    if (error) {
      return [];
    }
    
    if (property) {
      // Return a single "unit" representing the property
      return [{
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 1,
        square_feet: property.square_feet,
        price: property.min_price || property.max_price || 0,
        is_room_for_rent: false
      }];
    }
    
    return [];
  } catch (error) {
    return [];
  }
}



// Simple cache to prevent duplicate processing within a short time window
const recentlyProcessed = new Map<string, number>();
const PROCESSING_COOLDOWN = 5000; // 5 seconds

// Clean up old entries from the cache
function cleanupRecentlyProcessed() {
  const now = Date.now();
  for (const [key, timestamp] of recentlyProcessed.entries()) {
    if (now - timestamp > PROCESSING_COOLDOWN * 2) {
      recentlyProcessed.delete(key);
    }
  }
}

/**
 * Cache rental properties to the new rental tables
 */

export async function cacheRentalProperties(properties: Property[]): Promise<void> {
  if (!properties || properties.length === 0) {
    return;
  }

  try {

    
    // Process properties in batches to prevent overwhelming the database
    const BATCH_SIZE = 10; // Process 10 properties at a time
    const batches = [];
    
    for (let i = 0; i < properties.length; i += BATCH_SIZE) {
      batches.push(properties.slice(i, i + BATCH_SIZE));
    }



    let totalSuccessful = 0;
    let totalFailed = 0;
    
    for (const batch of batches) {
      try {
        // Clean up old cache entries periodically
        cleanupRecentlyProcessed();
        
        const results = await Promise.allSettled(
          batch.map(property => upsertRentalProperty(property))
        );
        
        // Count successful and failed operations in this batch
        const successful = results.filter(result => result.status === 'fulfilled').length;
        const failed = results.filter(result => result.status === 'rejected').length;
        

        
        totalSuccessful += successful;
        totalFailed += failed;
        
        if (failed > 0) {

          // Failed results
          results.forEach((result, index) => {
            if (result.status === 'rejected') {

            }
          });
        }
        
        // Small delay between batches to prevent overwhelming the database
        if (batches.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        // Error processing batch
        // Continue with next batch
      }
    }
    

    
    if (totalFailed > 0) {

    } else {

    }
  } catch (error) {

    throw error;
  }
} 

/**
 * Get current record counts in the rental database
 * Useful for verifying cleanup operations
 */
export async function getRentalDatabaseStats(): Promise<{
  properties: number;
  totalPhotos: number;
}> {
  try {
    // Getting database statistics...
    
    const propertiesResult = await supabase
      .from('rental_properties')
      .select('id', { count: 'exact', head: true });

    // Count total photos across all properties
    const { data: propertiesWithPhotos } = await supabase
      .from('rental_properties')
      .select('photos')
      .not('photos', 'is', null);

    let totalPhotos = 0;
    if (propertiesWithPhotos) {
      totalPhotos = propertiesWithPhotos.reduce((sum, property) => {
        return sum + (property.photos ? property.photos.length : 0);
      }, 0);
    }

    const stats = {
      properties: propertiesResult.count || 0,
      totalPhotos: totalPhotos
    };

    // Current database counts
    return stats;
    
  } catch (error) {
    // Error getting database stats
    return {
      properties: 0,
      totalPhotos: 0
    };
  }
}

/**
 * Check for duplicate ZPIDs in the database
 * This helps identify potential data integrity issues
 */
export async function checkForDuplicateZPIDs(): Promise<{ zpid: string; count: number }[]> {
  try {
    // Checking for duplicate ZPIDs...
    
    const { data: properties, error } = await supabase
      .from('rental_properties')
      .select('zpid')
      .not('zpid', 'is', null);
    
    if (error) {
      return [];
    }
    
    // Count occurrences of each ZPID
    const zpidCounts: { [key: string]: number } = {};
    properties?.forEach(prop => {
      if (prop.zpid) {
        zpidCounts[prop.zpid] = (zpidCounts[prop.zpid] || 0) + 1;
      }
    });
    
    // Find duplicates
    const duplicates = Object.entries(zpidCounts)
      .filter(([zpid, count]) => count > 1)
      .map(([zpid, count]) => ({ zpid, count }));
    
    if (duplicates.length > 0) {
      // Found duplicate ZPIDs
      // Duplicates found
    } else {
      // No duplicate ZPIDs found
    }
    
    return duplicates;
    
  } catch (error) {
    // Error checking for duplicates
    return [];
  }
}




/**
 * Clean up duplicate properties in the database
 * This removes all but the first occurrence of each ZPID
 */
export async function cleanupDuplicateProperties(): Promise<{ removed: number; kept: number }> {
  try {
    // Starting cleanup of duplicate properties...
    
    // Find duplicates
    const duplicates = await checkForDuplicateZPIDs();
    
    if (duplicates.length === 0) {
      // No duplicates found, cleanup not needed
      return { removed: 0, kept: 0 };
    }
    
    let totalRemoved = 0;
    let totalKept = 0;
    
    for (const { zpid, count } of duplicates) {
      if (count > 1) {
        // Keep the first record, remove the rest
        const { data: properties, error } = await supabase
          .from('rental_properties')
          .select('id')
          .eq('zpid', zpid)
          .order('id', { ascending: true });
        
        if (error || !properties || properties.length <= 1) {
          continue;
        }
        
        // Remove all but the first record
        const idsToRemove = properties.slice(1).map(p => p.id);
        const { error: deleteError } = await supabase
          .from('rental_properties')
          .delete()
          .in('id', idsToRemove);
        
        if (deleteError) {
          // Handle delete error silently
        } else {
          const removed = idsToRemove.length;
          totalRemoved += removed;
          totalKept += 1;
          // Cleanup completed successfully
        }
      }
    }
    
    // Cleanup completed successfully
    return { removed: totalRemoved, kept: totalKept };
  } catch (error) {
    // Handle cleanup error silently
    return { removed: 0, kept: 0 };
  }
} 